import React from 'react'

import { ZariContext } from '_zari/context'

import { Portlet, Table } from '_zari/core'

import * as CodigosTransaccionesConstant from 'constants/codigosTransacciones'
import TiposDeDocumentoSunatConstant from 'constants/tiposDeDocumentoSunat'

import * as ComprasService from 'services/compra.service'
import { DownloadAxiosFile, ZrSwal } from 'utils'

const portletBreadcrumbs = [{ name: 'Compras' }]

const tableHeaderRows = [
  { label: 'FECHA', width: 85, align: 'center' },
  { label: 'TIPO', width: 90, align: 'center' },
  {
    label: 'SERIE DOC',
    width: 105,
    align: 'center',
    filter: {
      type: 'text',
      name: 'serDoc'
    }
  },
  {
    label: 'NUM DOC',
    width: 105,
    align: 'center',
    filter: {
      type: 'text',
      name: 'numDoc'
    }
  },
  {
    label: 'RUC',
    width: 100,
    align: 'center'
  },
  { label: 'RAZÓN SOCIAL', align: 'center' },
  { label: '', width: 30, align: 'center' }
]

const ListaComprasPage = ({ location, history }) => {
  const __zariContext = React.useContext(ZariContext)

  const tablaRef = React.useRef(null)

  const flagPorGuias = React.useMemo(() => {
    return location.pathname === `/${CodigosTransaccionesConstant.COMPRASGUIAS_LISTA}`
  }, [location.pathname])

  const clickNuevo = React.useCallback(() => {
    if (flagPorGuias) {
      history.push(`/${CodigosTransaccionesConstant.COMPRASGUIAS_NUEVA}`)
    } else {
      history.push(`/${CodigosTransaccionesConstant.COMPRAS_NUEVO}`)
    }
  }, [flagPorGuias, history])

  const clickEditarHandler = React.useCallback(
    ({ _id }) => {
      if (flagPorGuias) {
        history.push(`/${CodigosTransaccionesConstant.COMPRASGUIAS_NUEVA}/${_id}`)
      } else {
        history.push(`/${CodigosTransaccionesConstant.COMPRAS_NUEVO}/${_id}`)
      }
    },
    [flagPorGuias, history]
  )

  const clickVerEstadoCuentaHandler = React.useCallback(
    ({ _id }) => {
      history.push(`/${CodigosTransaccionesConstant.COMPRAS_ESTADOCUENTA}/${_id}`)
    },
    [history]
  )

  const clickEliminar = React.useCallback(
    ({ _id }) => {
      ZrSwal.showAlert({
        tipo: 'warning',
        mensaje: 'Seguro de eliminar el registro?',
        showCerrar: true,
        onSuccess: () => {
          ComprasService.eliminarCompra(_id)
            .then(() => {
              __zariContext.snack('success', 'Registro eliminado.')
              tablaRef.current.reload()
            })
            .catch(({ data: { error } }) => __zariContext.snack('error', error))
        }
      })
    },
    [__zariContext]
  )

  const clickRegularizar = React.useCallback(
    ({ _id }) => {
      ZrSwal.showAlert({
        tipo: 'warning',
        mensaje: 'Seguro de regularizar el registro?',
        showCerrar: true,
        onSuccess: () => {
          ComprasService.regularizarAsientoxId(_id)
            .then(() => {
              __zariContext.snack('success', 'Registro regularizado.')
              tablaRef.current.reload()
            })
            .catch(({ data: { error } }) => __zariContext.snack('error', error))
        }
      })
    },
    [__zariContext]
  )

  const clickImprimirCompraxId = React.useCallback(async ({ _id }) => {
    await ComprasService.imprimirCompraxId(_id).then(({ data, headers }) => {
      DownloadAxiosFile.downloadAsBlob(data, headers)
    })
  }, [])

  const clickImprimirNotaCrdxId = React.useCallback(async ({ _id }) => {
    await ComprasService.imprimirNotaxId(_id).then(({ data, headers }) => {
      DownloadAxiosFile.downloadAsBlob(data, headers)
    })
  }, [])

  return (
    <>
      <Portlet key={flagPorGuias ? 'Compras por guias' : 'Compras'}>
        <Portlet.Header
          title={flagPorGuias ? 'Compras Por Guias' : 'Compras'}
          breadcrumbs={portletBreadcrumbs}
          buttons={[{ name: 'Nuevo', onClick: clickNuevo }]}
        />
        <Portlet.Body>
          <Table.ServerSide
            ref={tablaRef}
            useNest={true}
            useFilters={true}
            headRows={tableHeaderRows}
            url='/compras'
            baseParams={{
              flagPorOrden: false,
              flagPorGuias: flagPorGuias
            }}
            rowTemplate={row => (
              <Table.Row hover key={row._id}>
                <Table.Cell align='center'>{row.fechaEmision || '--'}</Table.Cell>
                <Table.Cell align='center'>
                  {row.tipoDocumento ? TiposDeDocumentoSunatConstant.__dictionary[row.tipoDocumento].nombre : '--'}
                </Table.Cell>
                <Table.Cell align='center'>{row.serieDocumento || '--'}</Table.Cell>
                <Table.Cell align='center'>{row.numeroDocumento || '--'}</Table.Cell>
                <Table.Cell align='center'>{row.documentoSocio || '--'}</Table.Cell>
                <Table.Cell>{row.nombreSocio || '--'}</Table.Cell>
                <Table.Cell>
                  <Table.Dropdown>
                    {({ handleClose }) => [
                      <Table.Dropdown.Item
                        key='editar'
                        handleClose={handleClose}
                        onClick={e => clickEditarHandler({ _id: row._id })}
                      >
                        Editar
                      </Table.Dropdown.Item>,
                      <Table.Dropdown.Item
                        key='eliminar'
                        handleClose={handleClose}
                        onClick={e => clickEliminar({ _id: row._id })}
                      >
                        Eliminar
                      </Table.Dropdown.Item>,
                      <Table.Dropdown.Item
                        key='estado-cuenta'
                        handleClose={handleClose}
                        onClick={e => clickVerEstadoCuentaHandler({ _id: row._id })}
                      >
                        Ver Estado de Cuenta
                      </Table.Dropdown.Item>,
                      <Table.Dropdown.Item
                        key='regularizar'
                        handleClose={handleClose}
                        onClick={e => clickRegularizar({ _id: row._id })}
                      >
                        Regularizar
                      </Table.Dropdown.Item>,
                      ...(row.tipoDocumento === '07'
                        ? [
                            <Table.Dropdown.Item
                              key='imprimir-nota'
                              handleClose={handleClose}
                              onClick={e => clickImprimirNotaCrdxId({ _id: row._id })}
                            >
                              Imprimir Nota
                            </Table.Dropdown.Item>
                          ]
                        : [
                            <Table.Dropdown.Item
                              key='imprimir'
                              handleClose={handleClose}
                              onClick={e => clickImprimirCompraxId({ _id: row._id })}
                            >
                              Imprimir
                            </Table.Dropdown.Item>
                          ])
                    ]}
                  </Table.Dropdown>
                </Table.Cell>
              </Table.Row>
            )}
          />
        </Portlet.Body>
      </Portlet>
    </>
  )
}

export default ListaComprasPage
